import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Mail, CheckCircle2, XCircle } from 'lucide-react';
import { apiService } from '@/services/api';

type PageType = 'unsubscribe' | 'resubscribe' | 'preferences' | 'error';

const EmailSubscription: React.FC = () => {
  const { type } = useParams<{ type: PageType }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const email = searchParams.get('email');
  const token = searchParams.get('token');

  const [preferences, setPreferences] = useState({
    newsletter: true,
    offers: true
  });

  useEffect(() => {
    // Fetch preferences if on preferences page
    if (type === 'preferences' && email) {
      apiService.get(`/mailing-list/preferences?email=${email}`, { isPublic: true })
        .then(data => {
          setPreferences({
            newsletter: data.newsletter,
            offers: data.offers
          });
        })
        .catch(() => navigate('/mailing-list/error'));
    }
  }, [type, email]);

  const handlePreferencesSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.post('/mailing-list/update-preferences', { email, ...preferences }, { isPublic: true });
      
      toast({
        title: 'Success',
        description: 'Your preferences have been updated.',
      });
    } catch {
      navigate('/mailing-list/error');
    }
  };

  const renderContent = () => {
    switch (type) {
      case 'unsubscribe':
        return (
          <>
            <Helmet>
              <title>Unsubscribed | SaunaDip</title>
            </Helmet>
            <div className="bg-white/90 rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border-t-8 border-sauna-orange">
              <Mail className="w-20 h-20 mx-auto mb-6 text-sauna-orange" />
              <h1 className="text-3xl font-bold mb-2 text-sauna-orange">You're Unsubscribed</h1>
              <p className="text-lg mb-4 text-sauna-navy/90">
                <span className="font-semibold">{email}</span> will no longer receive marketing communications from SaunaDip.
              </p>
              <p className="mb-6 text-sauna-navy/70">
                We're sad to see you go, but we respect your inbox!<br />
                If you ever want to return, you're always welcome back to the SaunaDip community.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-bold px-6 py-3 rounded-lg transition mb-2"
              >
                Go to Homepage
              </Button>
              <div className="mt-4 text-sauna-navy/50 text-sm">
                Changed your mind? <button onClick={() => navigate(`/mailing-list/resubscribe?email=${email}`)} className="underline hover:text-sauna-orange">Resubscribe</button>
              </div>
            </div>
          </>
        );

      case 'resubscribe':
        return (
          <>
            <Helmet>
              <title>Welcome Back! | SaunaDip</title>
            </Helmet>
            <div className="bg-white/90 rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border-t-8 border-sauna-orange">
              <CheckCircle2 className="w-20 h-20 mx-auto mb-6 text-sauna-orange" />
              <h1 className="text-3xl font-bold mb-2 text-sauna-orange">Welcome Back!</h1>
              <p className="text-lg mb-4 text-sauna-navy/90">
                <span className="font-semibold">{email}</span> is now resubscribed to SaunaDip updates.
              </p>
              <p className="mb-6 text-sauna-navy/70">
                We're delighted to have you back!<br />
                Expect the latest news, tips, and exclusive offers in your inbox soon.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-bold px-6 py-3 rounded-lg transition mb-2"
              >
                Go to Homepage
              </Button>
              <div className="mt-4 text-sauna-navy/50 text-sm">
                Want to manage your preferences? <button onClick={() => navigate(`/mailing-list/preferences?email=${email}`)} className="underline hover:text-sauna-orange">Update Preferences</button>
              </div>
            </div>
          </>
        );

      case 'preferences':
        return (
          <>
            <Helmet>
              <title>Manage Your Preferences | SaunaDip</title>
            </Helmet>
            <div className="bg-white/90 rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border-t-8 border-sauna-orange">
              <Mail className="w-20 h-20 mx-auto mb-6 text-sauna-orange" />
              <h1 className="text-3xl font-bold mb-2 text-sauna-orange">Manage Your Preferences</h1>
              <p className="text-lg mb-4 text-sauna-navy/90">
                Update your email preferences for <span className="font-semibold">{email}</span>.
              </p>
              <form onSubmit={handlePreferencesSubmit} className="space-y-4 text-left">
                <div className="space-y-4">
                  <label className="flex items-center gap-2">
                    <Checkbox 
                      checked={preferences.newsletter}
                      onCheckedChange={(checked) => 
                        setPreferences(prev => ({ ...prev, newsletter: checked as boolean }))
                      }
                    />
                    <span>Receive SaunaDip News & Tips</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <Checkbox 
                      checked={preferences.offers}
                      onCheckedChange={(checked) => 
                        setPreferences(prev => ({ ...prev, offers: checked as boolean }))
                      }
                    />
                    <span>Receive Exclusive Offers</span>
                  </label>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-sauna-orange hover:bg-sauna-orange/90"
                >
                  Save Preferences
                </Button>
              </form>
              <div className="mt-6 text-sauna-navy/50 text-sm">
                Want to unsubscribe? <button onClick={() => navigate(`/mailing-list/unsubscribe?email=${email}&token=${token}`)} className="underline hover:text-sauna-orange">Unsubscribe</button>
              </div>
            </div>
          </>
        );

      default:
        return (
          <>
            <Helmet>
              <title>Subscription Error | SaunaDip</title>
            </Helmet>
            <div className="bg-white/90 rounded-2xl shadow-xl p-8 max-w-lg w-full text-center border-t-8 border-red-400">
              <XCircle className="w-20 h-20 mx-auto mb-6 text-red-500" />
              <h1 className="text-3xl font-bold mb-2 text-red-500">Oops!</h1>
              <p className="text-lg mb-4 text-sauna-navy/90">
                We couldn't process your request.<br />
                The link may be invalid or expired.
              </p>
              <Button 
                onClick={() => navigate('/')}
                className="bg-sauna-orange hover:bg-sauna-orange/90 text-white font-bold px-6 py-3 rounded-lg transition mb-2"
              >
                Go to Homepage
              </Button>
              <div className="mt-4 text-sauna-navy/50 text-sm">
                Need help? <a href="mailto:hello@saunadip.com" className="underline hover:text-sauna-orange">Contact Support</a>
              </div>
            </div>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sauna-beige to-sauna-blue/10 py-8 px-4">
      {renderContent()}
    </div>
  );
};

export default EmailSubscription; 